/**
 * Shared debounce utility for performance optimization.
 * Consolidates the debounce implementations from all chart components.
 */

/**
 * Creates a debounced version of a function that delays execution
 * until after the specified delay has elapsed since the last call.
 *
 * @param {Function} fn - The function to debounce
 * @param {number} delay - Delay in milliseconds (default: 150)
 * @returns {Function} Debounced function
 *
 * @example
 * const handleResize = debounce(() => {
 *   setWindowWidth(window.innerWidth);
 * }, 150);
 *
 * window.addEventListener('resize', handleResize);
 */
export const debounce = (fn, delay = 150) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

/**
 * Creates a debounced function with a cancel method.
 * Useful when you need to clean up pending calls on unmount.
 *
 * @param {Function} fn - The function to debounce
 * @param {number} delay - Delay in milliseconds (default: 150)
 * @returns {Object} Object with debounced function and cancel method
 *
 * @example
 * const { debounced, cancel } = createCancellableDebounce(callback, 150);
 *
 * // In useEffect cleanup:
 * return () => cancel();
 */
export const createCancellableDebounce = (fn, delay = 150) => {
  let timeoutId;

  const debounced = (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };

  const cancel = () => {
    clearTimeout(timeoutId);
  };

  return { debounced, cancel };
};

export default debounce;
